import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ChevronUp, ChevronDown, Printer, Maximize, Minimize, ArrowRight,
  TrendingUp, Landmark, Percent, Users, Cpu, Target, Globe, Handshake,
  Wallet, Leaf, MessageCircle, Mountain, Home, Crown, Compass
} from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const sans = { fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" };
const mono = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

const WA_BRIEFING = `https://wa.me/919871155963?text=${encodeURIComponent('Requesting an executive briefing on the GoHimalayan Collective 100 proposal — joining the IHG group.')}`;
const WA_DATAROOM = `https://wa.me/919871155963?text=${encodeURIComponent('Requesting access to the GoHimalayan Collective 100 data room.')}`;

const SLIDES = [
  { id: 'slide-cover', label: 'Cover' },
  { id: 'slide-opportunity', label: 'The Opportunity' },
  { id: 'slide-problem', label: 'The Problem' },
  { id: 'slide-thread', label: 'The Thread' },
  { id: 'slide-model', label: 'The Fund Model' },
  { id: 'slide-phases', label: 'Phased Raise' },
  { id: 'slide-ihg', label: 'Within IHG' },
  { id: 'slide-os', label: 'GoHimalayan OS' },
  { id: 'slide-profile', label: 'Target Profile' },
  { id: 'slide-financials', label: 'Pro Forma' },
  { id: 'slide-ask', label: 'The Ask' },
  { id: 'slide-ihg-leads', label: 'IHG Leads' },
  { id: 'slide-movement', label: 'A Movement' },
];

/* ─────────── Logo marks — match live gohimalayan.com header (Mountain glyph +
   Playfair wordmark) and the Collective badge from bluehousebytheriver.com ─────────── */
const GoHimalayanMark = ({ className = '', wordmark = 'text-white', icon = 'text-[#6B9AC4]', size = 'md' }) => {
  const iconCls = size === 'lg' ? 'h-6 w-6' : 'h-4 w-4';
  const textCls = size === 'lg' ? 'text-xl' : 'text-sm';
  return (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="gohimalayan-mark">
      <Mountain className={`${iconCls} ${icon}`} strokeWidth={2.5} />
      <span className={`${textCls} font-bold tracking-tight ${wordmark}`} style={pf}>
        GoHimalayan
      </span>
    </div>
  );
};

/* Exact replica of the GoHimalayan Collective badge in the bluehousebytheriver.com
   footer (PARTNER panel): borderless — giant italic Playfair "100" centered behind,
   mountain glyph left, Playfair semibold "GoHimalayan" over "COLLECTIVE" caps. */
const Collective100Mark = ({ size = 'md' }) => {
  // Reference (bluehousebytheriver.com footer): wordmark 18px, "100" = text-8xl (96px)
  const s = size === 'lg'
    ? { num: 'text-[128px]', main: 'text-2xl', sub: 'text-[13px]', icon: 22 }
    : { num: 'text-8xl', main: 'text-lg', sub: 'text-[10px]', icon: 16 };
  return (
    <div className="relative inline-flex flex-col items-center" data-testid="collective-100-mark">
      <span
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${s.num} font-bold italic text-white/10 select-none pointer-events-none`}
        style={{ ...pf, transform: 'translate(-50%, -78%)' }}
        aria-hidden="true"
      >
        100
      </span>
      <div className="relative z-10 flex items-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={s.icon}
          height={s.icon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-white/70 flex-shrink-0"
        >
          <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
        </svg>
        <div className="flex flex-col items-center">
          <span className={`${s.main} font-semibold tracking-tight text-white`} style={pf}>
            GoHimalayan
          </span>
          <span className={`${s.sub} tracking-[0.2em] uppercase text-white/60 -mt-1`}>
            Collective
          </span>
        </div>
      </div>
    </div>
  );
};

const Eyebrow = ({ children }) => (
  <p className="text-[11px] uppercase tracking-[0.35em] text-[#E6C76A] mb-5" style={mono} data-testid="slide-eyebrow">
    {children}
  </p>
);

const SlideTitle = ({ children }) => (
  <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[1.08] mb-4" style={pf}>
    {children}
  </h2>
);

const SlideSub = ({ children }) => (
  <p className="text-base md:text-lg ihg-body leading-relaxed max-w-2xl" style={sans}>
    {children}
  </p>
);

const Stat = ({ value, label, testid }) => (
  <div className="border-l border-[#3F4A66] pl-4 sm:pl-6" data-testid={testid}>
    <div className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#E6C76A]" style={pf}>{value}</div>
    <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] ihg-micro" style={mono}>{label}</div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, body, testid }) => (
  <div
    className="group ihg-card border rounded-xl p-5 hover:border-[#E6C76A]/50 transition-colors duration-300"
    data-testid={testid}
  >
    <Icon className="w-5 h-5 text-[#E6C76A] mb-3 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
    <h3 className="text-sm font-semibold text-white tracking-wide mb-1.5" style={sans}>{title}</h3>
    <p className="text-xs sm:text-[13px] ihg-body leading-relaxed" style={sans}>{body}</p>
  </div>
);

export default function IHGPitch() {
  const containerRef = useRef(null);
  const slideRefs = useRef([]);
  const [active, setActive] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [finTab, setFinTab] = useState('unit');

  useEffect(() => {
    document.title = 'GoHimalayan Collective 100 — Within the IHG Group';
    return () => { document.title = 'GoHimalayan'; };
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = slideRefs.current.findIndex((el) => el === entry.target);
            if (idx !== -1) setActive(idx);
          }
        });
      },
      { root, threshold: 0.55 }
    );
    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const goTo = (i) => {
    const clamped = Math.max(0, Math.min(SLIDES.length - 1, i));
    slideRefs.current[clamped]?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (['BUTTON', 'A', 'INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (['ArrowRight', 'ArrowDown', 'PageDown', ' '].includes(e.key)) {
        e.preventDefault();
        setActive((a) => { goTo(a + 1); return a; });
      } else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(e.key)) {
        e.preventDefault();
        setActive((a) => { goTo(a - 1); return a; });
      } else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
      else if (e.key === 'End') { e.preventDefault(); goTo(SLIDES.length - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen();
  };

  const setSlideRef = (i) => (el) => { slideRefs.current[i] = el; };

  const slideClass = 'ihg-slide relative min-h-screen md:h-screen md:snap-start flex flex-col justify-center overflow-hidden';
  const innerClass = 'relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-16 md:py-8';

  return (
    <div className="bg-[#1A2235] text-white" style={sans} data-testid="ihg-pitch-page">
      <style>{`
        .ihg-deck { scrollbar-width: none; }
        .ihg-deck::-webkit-scrollbar { display: none; }
        .ihg-grain::after {
          content: ''; position: absolute; inset: 0; pointer-events: none; opacity: 0.4;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.04 0'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)'/%3E%3C/svg%3E");
        }
        .ihg-slide { background: #1A2235; }
        .ihg-body { color: #D1D5DB; }
        .ihg-mute { color: #9CA3AF; }
        .ihg-micro { color: #94A3B8; }
        .ihg-card { background: rgba(40, 51, 72, 0.9); border-color: #3F4A66; }
        @keyframes ihg-pulse-dot { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
        .ihg-flow-dot { animation: ihg-pulse-dot 1.8s ease-in-out infinite; }
        @media print {
          @page { size: A4 landscape; margin: 0; }
          html, body { background: #1A2235 !important; }
          .ihg-deck { height: auto !important; overflow: visible !important; scroll-snap-type: none !important; }
          .ihg-slide {
            height: 100vh !important; min-height: 100vh !important;
            page-break-after: always; break-after: page; overflow: hidden !important;
          }
          #slide-financials { height: auto !important; overflow: visible !important; }
          #slide-financials .fin-panel { display: block !important; }
          .ihg-chrome { display: none !important; }
          .ihg-slide, .ihg-slide * {
            -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* ── Fixed chrome ─────────────────────────────── */}
      <header className="ihg-chrome fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 sm:px-8 py-4 bg-[#1A2235]/70 backdrop-blur-md border-b border-[#3F4A66]/60">
        <div className="flex items-center gap-4">
          <Link
            to="/links"
            className="flex items-center gap-2 ihg-body hover:text-[#E6C76A] transition-colors text-[11px] uppercase tracking-[0.2em]"
            style={mono}
            data-testid="deck-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">All Links</span>
          </Link>
          <span className="hidden md:block text-[#263248]">|</span>
          <GoHimalayanMark className="hidden md:inline-flex" />
        </div>
        <div className="flex items-center gap-3 sm:gap-5">
          <div className="flex items-center gap-3" data-testid="deck-counter">
            <span className="text-[11px] tracking-[0.25em] ihg-body" style={mono}>
              {String(active + 1).padStart(2, '0')} <span className="ihg-micro">/ {String(SLIDES.length).padStart(2, '0')}</span>
            </span>
            <div className="hidden sm:block w-20 h-px bg-[#263248] relative overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full bg-[#E6C76A] transition-all duration-500"
                style={{ width: `${((active + 1) / SLIDES.length) * 100}%` }}
                data-testid="deck-progress-line"
              />
            </div>
          </div>
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-full border border-[#3F4A66] ihg-body hover:text-[#E6C76A] hover:border-[#E6C76A]/50 transition-colors"
            aria-label="Toggle fullscreen"
            data-testid="deck-fullscreen-btn"
          >
            {isFullscreen ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => window.print()}
            className="p-2 rounded-full border border-[#3F4A66] ihg-body hover:text-[#E6C76A] hover:border-[#E6C76A]/50 transition-colors"
            aria-label="Download PDF"
            data-testid="deck-print-btn"
          >
            <Printer className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* Dot navigation (desktop) */}
      <nav className="ihg-chrome hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-50 flex-col gap-3" data-testid="deck-dot-nav">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => goTo(i)}
            className="group relative flex items-center justify-end"
            aria-label={`Go to ${s.label}`}
            data-testid={`deck-dot-${i + 1}`}
          >
            <span className="absolute right-6 whitespace-nowrap text-[10px] uppercase tracking-[0.2em] ihg-body opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#283348] border border-[#3F4A66] rounded px-2 py-1" style={mono}>
              {s.label}
            </span>
            <span className={`block rounded-full transition-all duration-300 ${active === i ? 'w-2.5 h-2.5 bg-[#E6C76A] ring-4 ring-[#E6C76A]/20' : 'w-1.5 h-1.5 bg-[#263248] group-hover:bg-[#8C7326]'}`} />
          </button>
        ))}
      </nav>

      {/* Prev / next (desktop) */}
      <div className="ihg-chrome hidden md:flex fixed bottom-6 left-1/2 -translate-x-1/2 z-50 items-center gap-2">
        <button
          onClick={() => goTo(active - 1)}
          disabled={active === 0}
          className="p-2.5 rounded-full border border-[#3F4A66] bg-[#283348]/90 backdrop-blur ihg-body hover:text-[#E6C76A] hover:border-[#E6C76A]/50 transition-colors disabled:opacity-30 disabled:hover:ihg-body disabled:hover:border-[#3F4A66]"
          aria-label="Previous slide"
          data-testid="deck-prev-btn"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <button
          onClick={() => goTo(active + 1)}
          disabled={active === SLIDES.length - 1}
          className="p-2.5 rounded-full border border-[#3F4A66] bg-[#283348]/90 backdrop-blur ihg-body hover:text-[#E6C76A] hover:border-[#E6C76A]/50 transition-colors disabled:opacity-30 disabled:hover:ihg-body disabled:hover:border-[#3F4A66]"
          aria-label="Next slide"
          data-testid="deck-next-btn"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* ── Deck ─────────────────────────────── */}
      <div ref={containerRef} className="ihg-deck h-screen overflow-y-auto md:snap-y md:snap-mandatory">

        {/* 1 · COVER */}
        <section ref={setSlideRef(0)} id="slide-cover" className={`${slideClass} ihg-grain`} data-testid="slide-cover-container">
          <img
            src="https://images.unsplash.com/photo-1742311313666-4161d9039147?w=1600&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-[#1A2235]/60" />
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#E6C76A]/10 blur-[140px]" />
          <div className={innerClass}>
            <div className="flex items-center gap-4 mb-8">
              <GoHimalayanMark size="lg" />
              <span className="text-[#263248]">×</span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-white/80" style={mono}>IHG Hotels & Resorts</span>
            </div>
            <span className="inline-block border border-[#E6C76A]/40 text-[#E6C76A] text-[10px] uppercase tracking-[0.3em] px-4 py-2 rounded-full mb-8" style={mono} data-testid="cover-confidential-badge">
              Strictly Confidential · For Institutional Review
            </span>
            <div className="mb-6"><Collective100Mark size="lg" /></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white mb-6" style={pf}>
              The Collective <em className="italic text-[#E6C76A]">100</em>
            </h1>
            <p className="text-base md:text-lg ihg-body leading-relaxed max-w-2xl mb-12" style={sans}>
              An opportunity for the IHG group to plant the first global flag in the
              Himalayan intimate-luxury category — through <em className="italic text-white">GoHimalayan.com</em>,
              operating a curated Collective of 100 host-driven boutique houses and a
              travel-experience portal for the mountains. Backed by an $18.5M (₹175 Cr)
              private equity fund acquiring up to 50% equity in each house.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-4xl">
              <Stat value="$18.5M" label="Fund corpus · AUM target (₹175 Cr)" testid="cover-stat-fund" />
              <Stat value="100" label="Intimate properties · 6–12 keys" testid="cover-stat-properties" />
              <Stat value="40–50%" label="Equity per asset" testid="cover-stat-equity" />
              <Stat value="$185K" label="Average injection (₹1.75 Cr)" testid="cover-stat-injection" />
            </div>
            <p className="mt-12 text-[10px] uppercase tracking-[0.3em] ihg-micro" style={mono}>
              Powered by GoHimalayan OS · October 2026
            </p>
          </div>
        </section>

        {/* 2 · OPPORTUNITY */}
        <section ref={setSlideRef(1)} id="slide-opportunity" className={slideClass} data-testid="slide-opportunity-container">
          <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#E6C76A]/5 blur-[120px]" />
          <div className={innerClass}>
            <Eyebrow>02 · The Opportunity</Eyebrow>
            <SlideTitle>The Himalayan Opportunity</SlideTitle>
            <SlideSub>
              An underserved $1.27 billion (₹12,000 Cr) luxury ecotourism ecosystem — still
              waiting for its first institutional owner.
            </SlideSub>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mt-10 max-w-5xl">
              <Stat value="100+" label="High-demand valleys" testid="opp-stat-valleys" />
              <Stat value="4.2×" label="Growth in experiential travel" testid="opp-stat-growth" />
              <Stat value="&lt;5%" label="Institutional branded supply" testid="opp-stat-supply" />
              <Stat value="$148→$370" label="ADR headroom / night (₹14K→₹35K)" testid="opp-stat-adr" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10 max-w-5xl">
              {[
                { region: 'Himachal Pradesh', valleys: 'Tirthan · Spiti · Kullu' },
                { region: 'Uttarakhand', valleys: 'Kumaon · Garhwal' },
                { region: 'Ladakh', valleys: 'Leh · Nubra' },
                { region: 'Sikkim & Northeast', valleys: 'Gangtok · Darjeeling Hills' },
              ].map((c) => (
                <div key={c.region} className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4 hover:border-[#E6C76A]/40 transition-colors" data-testid={`opp-cluster-${c.region.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <Mountain className="w-4 h-4 text-[#E6C76A] mb-2" strokeWidth={1.5} />
                  <p className="text-sm font-semibold text-white" style={sans}>{c.region}</p>
                  <p className="text-[11px] ihg-micro mt-1" style={mono}>{c.valleys}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3 · PROBLEM */}
        <section ref={setSlideRef(2)} id="slide-problem" className={slideClass} data-testid="slide-problem-container">
          <div className={innerClass}>
            <Eyebrow>03 · The Problem</Eyebrow>
            <SlideTitle>The Independent Owner Dilemma</SlideTitle>
            <SlideSub>World-class homes. Sub-institutional outcomes.</SlideSub>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              <FeatureCard icon={Wallet} title="Capital starvation" body="No access to growth capital for capex, standards upgrades, or working-capital buffers through the off-season." testid="problem-card-capital" />
              <FeatureCard icon={Percent} title="OTA dependence" body="22–30% commissions erode margin; the guest relationship belongs to the platform, never the property." testid="problem-card-ota" />
              <FeatureCard icon={Cpu} title="Fragmented operations" body="No PMS, manual yield management, and seasonal occupancy swings absorbed alone." testid="problem-card-ops" />
              <FeatureCard icon={Users} title="Succession gap" body="Second-generation custodians want professional asset management — not day-to-day operations." testid="problem-card-succession" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 max-w-4xl">
              <div className="border border-[#3F4A66] rounded-xl p-5" data-testid="problem-standalone-card">
                <p className="text-[10px] uppercase tracking-[0.25em] ihg-micro mb-3" style={mono}>A standalone asset</p>
                <ul className="space-y-1.5 text-[13px] ihg-body" style={sans}>
                  <li>Distribution: two OTAs and a phone number</li>
                  <li>Loyalty: none — every guest is a stranger</li>
                  <li>Yield: static seasonal rate card</li>
                  <li>Brand: invisible beyond the valley</li>
                </ul>
              </div>
              <div className="border border-[#E6C76A]/40 bg-[#E6C76A]/5 rounded-xl p-5" data-testid="problem-collective-card">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#E6C76A] mb-3" style={mono}>The same asset, inside GoHimalayan × IHG</p>
                <ul className="space-y-1.5 text-[13px] text-white/90" style={sans}>
                  <li>Distribution: IHG global CRS + direct</li>
                  <li>Loyalty: 130M+ IHG One Rewards members</li>
                  <li>Yield: AI dynamic pricing, valley-aware</li>
                  <li>Brand: A dedicated Himalayan collection within IHG</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 4 · THE THREAD — platform × property */}
        <section ref={setSlideRef(3)} id="slide-thread" className={slideClass} data-testid="slide-thread-container">
          <div className={innerClass}>
            <Eyebrow>04 · The Thread</Eyebrow>
            <SlideTitle>From Platform to Property</SlideTitle>
            <SlideSub>
              GoHimalayan is already the discovery layer. Bluehouse by the River is the
              first house. Both exist, both trade, both are ready to join IHG.
            </SlideSub>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10 max-w-5xl">
              <a
                href="https://gohimalayan.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-xl overflow-hidden border border-[#3F4A66] hover:border-[#E6C76A]/50 transition-colors"
                data-testid="thread-gohimalayan-card"
              >
                <div className="relative aspect-[16/10] bg-[#1A2235] overflow-hidden">
                  <img
                    src="/snapshot-gohimalayan.jpg"
                    alt="gohimalayan.com homepage"
                    className="absolute inset-0 w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-[#1A2235]/70 backdrop-blur-sm px-2.5 py-1 rounded-md border border-[#3F4A66]">
                    <span className="w-2 h-2 rounded-full bg-[#E6C76A]" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white" style={mono}>gohimalayan.com</span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>The platform</p>
                  <p className="text-base font-semibold text-white mb-1" style={sans}>GoHimalayan</p>
                  <p className="text-[12px] ihg-body" style={sans}>
                    Over 100 selected stays listed and growing across the Himalayas · AI-curated storytelling · direct bookings · the content and demand engine for the Collective
                  </p>
                </div>
              </a>

              <a
                href="https://www.bluehousebytheriver.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-xl overflow-hidden border border-[#3F4A66] hover:border-[#E6C76A]/50 transition-colors"
                data-testid="thread-bluehouse-card"
              >
                <div className="relative aspect-[16/10] bg-[#1A2235] overflow-hidden">
                  <img
                    src="/snapshot-bluehouse.jpg"
                    alt="bluehousebytheriver.com homepage"
                    className="absolute inset-0 w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-[#1A2235]/70 backdrop-blur-sm px-2.5 py-1 rounded-md border border-[#3F4A66]">
                    <span className="w-2 h-2 rounded-full bg-[#E6C76A]" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white" style={mono}>bluehousebytheriver.com</span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>The first house</p>
                  <p className="text-base font-semibold text-white mb-1" style={sans}><em className="italic" style={pf}>Bluehouse by the River</em> · Tirthan Valley</p>
                  <p className="text-[12px] ihg-body" style={sans}>
                    8 keys · timber & stone · host-run · 4.4★ · a prototype of the Collective asset — the exact profile this fund will acquire 100 of
                  </p>
                </div>
              </a>
            </div>
            <p className="mt-6 text-[11px] ihg-micro tracking-wide max-w-3xl" style={mono}>
              The thread is already pulled. The fund scales it.
            </p>
          </div>
        </section>

        {/* 5 · FUND MODEL */}
        <section ref={setSlideRef(4)} id="slide-model" className={slideClass} data-testid="slide-model-container">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-[#E6C76A]/5 blur-[120px]" />
          <div className={innerClass}>
            <Eyebrow>05 · The Fund Model</Eyebrow>
            <SlideTitle>The Collective 100 Fund</SlideTitle>
            <SlideSub>Smaller cheques. Deeper intimacy. Hundred-fold scale.</SlideSub>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mt-10 max-w-5xl">
              <Stat value="$18.5M" label="Fund blueprint (₹175 Cr)" testid="model-stat-fund" />
              <Stat value="$185K" label="Avg equity injection (₹1.75 Cr)" testid="model-stat-injection" />
              <Stat value="40–50%" label="Equity + management rights" testid="model-stat-equity" />
              <Stat value="$63K" label="Capex + OS per asset (₹60 L)" testid="model-stat-capex" />
            </div>
            <div className="mt-12 max-w-5xl">
              <p className="text-[10px] uppercase tracking-[0.25em] ihg-micro mb-4" style={mono}>Value creation waterfall</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="model-waterfall">
                {[
                  { n: '01', t: 'Capital injection', d: '$185K (₹1.75 Cr) per asset' },
                  { n: '02', t: 'Heritage renovation', d: 'Capex to brand standard · host preserved' },
                  { n: '03', t: 'Global distribution', d: 'A Himalayan brand within IHG' },
                  { n: '04', t: 'Yield & stewardship', d: 'RevPAR × margin × ESG' },
                ].map((step, i) => (
                  <React.Fragment key={step.n}>
                    <div className="flex-1 bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4 hover:border-[#E6C76A]/50 transition-colors">
                      <p className="text-[#E6C76A] text-xs mb-1" style={mono}>{step.n}</p>
                      <p className="text-sm font-semibold text-white" style={sans}>{step.t}</p>
                      <p className="text-[11px] ihg-micro mt-0.5" style={sans}>{step.d}</p>
                    </div>
                    {i < 3 && <ArrowRight className="hidden sm:block w-4 h-4 text-[#8C7326] shrink-0" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 6 · PHASED RAISE */}
        <section ref={setSlideRef(5)} id="slide-phases" className={slideClass} data-testid="slide-phases-container">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-full bg-[#E6C76A]/5 blur-[120px]" />
          <div className={innerClass}>
            <Eyebrow>06 · Phased Deployment</Eyebrow>
            <SlideTitle>Three Tranches. Not One Cheque.</SlideTitle>
            <SlideSub>
              The $18.5M (₹175 Cr) corpus is raised in stages — each tranche de-risked by
              the one before it. No LP writes a cheque against a thesis; every cheque
              writes against proof.
            </SlideSub>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-10 max-w-6xl" data-testid="phases-grid">
              {/* Phase I */}
              <div className="relative bg-[#E6C76A]/8 border border-[#E6C76A]/50 rounded-xl p-5 overflow-hidden" data-testid="phase-i-card">
                <span className="absolute -top-3 -right-3 bg-[#E6C76A] text-[#0B0F17] text-[9px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full" style={mono}>Live</span>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#E6C76A] mb-2" style={mono}>Phase I · Prove</p>
                <p className="text-2xl text-white mb-1" style={pf}>$3 – $5M</p>
                <p className="text-[11px] ihg-micro mb-4" style={mono}>₹28 – ₹47 Cr · 10 houses</p>
                <ul className="space-y-1.5 text-[12px] text-white/90 leading-relaxed" style={sans}>
                  <li>• Bluehouse by the River as the flagship IHG property</li>
                  <li>• 9 additional pilot acquisitions · Tirthan, Kumaon</li>
                  <li>• GoHimalayan OS integrated with IHG CRS</li>
                  <li>• Q4 2026 → Q2 2027 · 6 months</li>
                </ul>
                <div className="mt-4 pt-3 border-t border-[#E6C76A]/20">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A]" style={mono}>De-risk milestone</p>
                  <p className="text-[11px] text-white/85 mt-1" style={sans}>
                    First IHG-flagged house live · trading data unlocks Phase II
                  </p>
                </div>
              </div>

              {/* Phase II */}
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-5" data-testid="phase-ii-card">
                <p className="text-[10px] uppercase tracking-[0.25em] ihg-body mb-2" style={mono}>Phase II · Scale</p>
                <p className="text-2xl text-white mb-1" style={pf}>$5 – $7M</p>
                <p className="text-[11px] ihg-micro mb-4" style={mono}>₹47 – ₹66 Cr · next 30 houses</p>
                <ul className="space-y-1.5 text-[12px] ihg-body leading-relaxed" style={sans}>
                  <li>• Portfolio expansion into Spiti, Ladakh, Sikkim</li>
                  <li>• Second close triggered by Phase I RevPAR uplift</li>
                  <li>• Institutional LPs join the cap table</li>
                  <li>• Q3 2027 → Q2 2028 · 12 months</li>
                </ul>
                <div className="mt-4 pt-3 border-t border-[#3F4A66]">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A]" style={mono}>De-risk milestone</p>
                  <p className="text-[11px] ihg-body mt-1" style={sans}>
                    40 houses live · 2 yrs operating data · Phase III priced on performance
                  </p>
                </div>
              </div>

              {/* Phase III */}
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-5" data-testid="phase-iii-card">
                <p className="text-[10px] uppercase tracking-[0.25em] ihg-body mb-2" style={mono}>Phase III · Lead</p>
                <p className="text-2xl text-white mb-1" style={pf}>$7 – $9M</p>
                <p className="text-[11px] ihg-micro mb-4" style={mono}>₹66 – ₹85 Cr · final 60 houses</p>
                <ul className="space-y-1.5 text-[12px] ihg-body leading-relaxed" style={sans}>
                  <li>• Complete the Collective 100 across 8+ valleys</li>
                  <li>• Northeast India + cross-border (Nepal, Bhutan)</li>
                  <li>• Priced at a step-up — proven asset class</li>
                  <li>• Q3 2028 → Q4 2029 · 18 months</li>
                </ul>
                <div className="mt-4 pt-3 border-t border-[#3F4A66]">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A]" style={mono}>Exit horizon</p>
                  <p className="text-[11px] ihg-body mt-1" style={sans}>
                    REIT listing or strategic sale · Yr 7–10 · target net IRR ~20%
                  </p>
                </div>
              </div>
            </div>

            {/* Cumulative ramp bar */}
            <div className="mt-8 max-w-6xl" data-testid="phases-ramp">
              <p className="text-[10px] uppercase tracking-[0.2em] ihg-micro mb-3" style={mono}>Cumulative deployment</p>
              <div className="relative h-2 bg-[#283348] rounded-full overflow-hidden border border-[#3F4A66]">
                <div className="absolute left-0 top-0 h-full bg-[#E6C76A]" style={{ width: '22%' }} />
                <div className="absolute left-[22%] top-0 h-full bg-[#E6C76A]/70" style={{ width: '33%' }} />
                <div className="absolute left-[55%] top-0 h-full bg-[#E6C76A]/40" style={{ width: '45%' }} />
              </div>
              <div className="grid grid-cols-3 mt-2 text-[10px]" style={mono}>
                <span className="text-[#E6C76A]">$4M · 10 houses · 2027</span>
                <span className="text-[#E6C76A]/80 text-center">$10M · 40 houses · 2028</span>
                <span className="text-[#E6C76A]/60 text-right">$18.5M · 100 houses · 2029</span>
              </div>
            </div>

            <p className="mt-6 text-[11px] ihg-micro max-w-3xl leading-relaxed" style={mono} data-testid="phases-rationale">
              Each tranche is small enough to find anchor LPs quickly, and the capital
              compounds against trading data — not against a slide.
            </p>
          </div>
        </section>

        {/* 7 · WITHIN IHG */}
        <section ref={setSlideRef(6)} id="slide-ihg" className={slideClass} data-testid="slide-ihg-synergy-container">
          <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#E6C76A]/5 blur-[130px]" />
          <div className={innerClass}>
            <Eyebrow>07 · Within IHG</Eyebrow>
            <SlideTitle>GoHimalayan — a Himalayan Brand<br />Inside the IHG Group.</SlideTitle>
            <SlideSub>
              Not a soft-brand flag on 100 unrelated hotels. A dedicated Himalayan sub-brand
              operating two tightly-linked assets inside IHG — a Collection of 100 houses,
              and the region&rsquo;s definitive travel-experience portal.
            </SlideSub>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 max-w-5xl" data-testid="within-ihg-pillars">
              <div className="bg-[#E6C76A]/8 border border-[#E6C76A]/40 rounded-xl p-5">
                <Landmark className="w-5 h-5 text-[#E6C76A] mb-3" strokeWidth={1.5} />
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1.5" style={mono}>Pillar 01</p>
                <p className="text-base font-semibold text-white mb-1" style={sans}>The Collective 100 — a new IHG Collection</p>
                <p className="text-[12px] text-white/85 leading-relaxed" style={sans}>
                  100 intimate (6–12 keys) host-driven houses across the Indian Himalaya,
                  curated under a dedicated GoHimalayan Collection within IHG. Soft-brand or
                  own-brand — IHG decides the architecture.
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-5">
                <Compass className="w-5 h-5 text-[#E6C76A] mb-3" strokeWidth={1.5} />
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1.5" style={mono}>Pillar 02</p>
                <p className="text-base font-semibold text-white mb-1" style={sans}>GoHimalayan.com — the Himalayan travel portal</p>
                <p className="text-[12px] ihg-body leading-relaxed" style={sans}>
                  The region&rsquo;s definitive discovery, planning and booking surface — valleys,
                  journeys, AI trip planning, artisan commerce — all funnelling demand into IHG
                  properties with IHG One Rewards attached.
                </p>
              </div>
            </div>

            <p className="mt-8 text-[10px] uppercase tracking-[0.25em] ihg-micro" style={mono}>Brand positioning options</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 max-w-5xl" data-testid="within-ihg-options">
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <p className="text-xs font-semibold text-white" style={sans}>Option A · Vignette Collection</p>
                <p className="text-[11px] ihg-body mt-1 leading-snug" style={sans}>
                  Soft-brand each house under Vignette · fastest to flag · identity preserved
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <p className="text-xs font-semibold text-white" style={sans}>Option B · A new IHG sub-brand</p>
                <p className="text-[11px] ihg-body mt-1 leading-snug" style={sans}>
                  Launch &ldquo;GoHimalayan by IHG&rdquo; — a dedicated Himalayan collection, exportable globally
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <p className="text-xs font-semibold text-white" style={sans}>Option C · Hotel Indigo / Garner</p>
                <p className="text-[11px] ihg-body mt-1 leading-snug" style={sans}>
                  Mapped to the existing IHG brand that best fits each house&rsquo;s positioning
                </p>
              </div>
            </div>

            <p className="mt-6 text-[11px] ihg-micro max-w-3xl leading-relaxed" style={mono}>
              The thesis is brand-agnostic. GoHimalayan brings the inventory, the content,
              the operating layer and the demand funnel. IHG decides which flag flies on each roof.
            </p>
          </div>
        </section>

        {/* 8 · GOHIMALAYAN OS */}
        <section ref={setSlideRef(7)} id="slide-os" className={slideClass} data-testid="slide-tech-stack-container">
          <div className={innerClass}>
            <Eyebrow>08 · The Middleware</Eyebrow>
            <SlideTitle>GoHimalayan OS</SlideTitle>
            <SlideSub>
              The proprietary middleware layer between a heritage homestay and IHG&rsquo;s
              global systems.
            </SlideSub>
            <div className="mt-10 max-w-4xl" data-testid="os-flow-diagram">
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
                <div className="flex-1 border border-[#3F4A66] bg-[#283348]/90 rounded-xl p-4 text-center">
                  <p className="text-[10px] uppercase tracking-[0.2em] ihg-micro mb-1" style={mono}>On property</p>
                  <p className="text-sm font-semibold text-white" style={sans}>Local PMS & owner app</p>
                </div>
                <div className="relative flex items-center justify-center md:w-16 h-8 md:h-auto">
                  <div className="hidden md:block w-full border-t-2 border-dashed border-[#8C7326]/60" />
                  <div className="md:hidden h-full border-l-2 border-dashed border-[#8C7326]/60" />
                  <span className="ihg-flow-dot absolute w-2 h-2 rounded-full bg-[#E6C76A]" />
                </div>
                <div className="flex-1 border border-[#E6C76A]/60 bg-[#E6C76A]/10 rounded-xl p-4 text-center">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>The layer we own</p>
                  <p className="text-sm font-semibold text-white" style={sans}>GoHimalayan OS</p>
                </div>
                <div className="relative flex items-center justify-center md:w-16 h-8 md:h-auto">
                  <div className="hidden md:block w-full border-t-2 border-dashed border-[#8C7326]/60" />
                  <div className="md:hidden h-full border-l-2 border-dashed border-[#8C7326]/60" />
                  <span className="ihg-flow-dot absolute w-2 h-2 rounded-full bg-[#E6C76A]" style={{ animationDelay: '0.9s' }} />
                </div>
                <div className="flex-1 border border-[#3F4A66] bg-[#283348]/90 rounded-xl p-4 text-center">
                  <p className="text-[10px] uppercase tracking-[0.2em] ihg-micro mb-1" style={mono}>Global systems</p>
                  <p className="text-sm font-semibold text-white" style={sans}>IHG CRS · Opera · GDS</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              <FeatureCard icon={TrendingUp} title="AI seasonal pricing" body="Dynamic yield tuned to Himalayan surge windows — season by season, valley by valley." testid="os-card-pricing" />
              <FeatureCard icon={Cpu} title="Inventory sync" body="Real-time rates and availability across CRS, GDS, and direct channels from one pane." testid="os-card-sync" />
              <FeatureCard icon={Wallet} title="Automated settlement" body="Transparent owner payouts and fund-level reporting, reconciled automatically." testid="os-card-settlement" />
              <FeatureCard icon={Leaf} title="ESG & carbon tracking" body="Per-stay carbon accounting — institutional-grade reporting for the fund and the brand." testid="os-card-esg" />
            </div>
          </div>
        </section>

        {/* 9 · TARGET PROFILE */}
        <section ref={setSlideRef(8)} id="slide-profile" className={slideClass} data-testid="slide-target-profile-container">
          <div className={innerClass}>
            <Eyebrow>09 · Acquisition Criteria</Eyebrow>
            <SlideTitle>Target Property Profile</SlideTitle>
            <SlideSub>One hundred intimate houses. One rigorous filter.</SlideSub>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-10 items-stretch">
              <div className="lg:col-span-5 relative rounded-xl overflow-hidden border border-[#3F4A66] min-h-[220px]" data-testid="profile-image-card">
                <img
                  src="https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=1200&q=85"
                  alt="Himalayan boutique property at dawn"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#1A2235]/70 via-[#1A2235]/20 to-transparent" />
                <p className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.25em] text-white" style={mono}>
                  Host-run riverside estate · Tirthan Valley
                </p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { k: 'Keys', v: '6 – 12', d: 'Intimate boutique scale — never a hotel' },
                  { k: 'Current ARR', v: '$127 – $296', d: 'Per night, pre-acquisition (₹12K – ₹28K)' },
                  { k: 'Destinations', v: 'High-barrier', d: 'Spiti · Tirthan · Nubra · Kumaon · Gangtok' },
                  { k: 'Architecture', v: 'Locale-rooted', d: 'Stone · timber · mud · regional vernacular — never generic' },
                  { k: 'Owner profile', v: 'Host-driven', d: '2nd-gen custodians who still greet every guest personally' },
                  { k: 'Sustainability', v: 'Non-negotiable', d: 'Solar · water harvesting · local supply chains · low-impact capex' },
                ].map((item) => (
                  <div key={item.k} className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4 hover:border-[#E6C76A]/40 transition-colors" data-testid={`profile-spec-${item.k.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <p className="text-[10px] uppercase tracking-[0.2em] ihg-micro" style={mono}>{item.k}</p>
                    <p className="text-lg text-[#E6C76A] mt-1" style={pf}>{item.v}</p>
                    <p className="text-[11px] ihg-body mt-1" style={sans}>{item.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 10 · FINANCIALS */}
        <section ref={setSlideRef(9)} id="slide-financials" className={slideClass} data-testid="slide-financials-container">
          <div className={innerClass}>
            <Eyebrow>10 · Financial Pro Forma</Eyebrow>
            <SlideTitle>Per-Asset Economics & Fund Returns</SlideTitle>
            <div className="ihg-chrome inline-flex mt-6 border border-[#3F4A66] rounded-full p-1 bg-[#283348]/90" data-testid="fin-tab-toggle">
              {[
                { key: 'unit', label: 'Unit economics' },
                { key: 'fund', label: 'Fund returns' },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setFinTab(t.key)}
                  className={`px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.15em] transition-colors ${finTab === t.key ? 'bg-[#E6C76A] text-[#0B0F17] font-semibold' : 'ihg-body hover:text-white'}`}
                  style={mono}
                  data-testid={`fin-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className={`fin-panel mt-6 max-w-4xl ${finTab === 'unit' ? 'block' : 'hidden'}`} data-testid="fin-panel-unit">
              <table className="w-full text-left border border-[#3F4A66] rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-[#283348]">
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] ihg-micro" style={mono}>Metric</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] ihg-micro" style={mono}>Pre-acquisition</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#E6C76A]" style={mono}>Post · Year 3</th>
                  </tr>
                </thead>
                <tbody className="text-[13px]" style={sans}>
                  {[
                    ['Keys per property', '6 – 12', '6 – 12 (preserved)'],
                    ['Average daily rate', '$153 (₹14,500)', '$254 (₹24,000)'],
                    ['Occupancy', '38%', '58%'],
                    ['RevPAR', '$58 (₹5,510)', '$147 (₹13,920)'],
                    ['EBITDA margin', '22%', '40%'],
                    ['OTA share of bookings', '70%', '25%'],
                  ].map(([m, pre, post]) => (
                    <tr key={m} className="border-t border-[#3F4A66]/60">
                      <td className="px-4 py-2 ihg-body">{m}</td>
                      <td className="px-4 py-2 ihg-micro" style={mono}>{pre}</td>
                      <td className="px-4 py-2 text-white" style={mono}>{post}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={`fin-panel mt-6 max-w-4xl ${finTab === 'fund' ? 'block' : 'hidden'}`} data-testid="fin-panel-fund">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  ['$18.5M (₹175 Cr)', 'Fund corpus'],
                  ['$17.6M (₹166 Cr)', 'Deployed · 95%'],
                  ['26.0%', 'Target gross IRR'],
                  ['3.4×', 'Target MOIC'],
                  ['~20%', 'Target net IRR'],
                  ['7–10 yrs', 'Hold period'],
                  ['100', 'Assets at exit'],
                  ['REIT / sale', 'Exit routes'],
                ].map(([v, l]) => (
                  <div key={l} className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-3.5">
                    <p className="text-base sm:text-lg text-[#E6C76A]" style={pf}>{v}</p>
                    <p className="text-[10px] uppercase tracking-[0.15em] ihg-micro mt-1" style={mono}>{l}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 max-w-4xl" data-testid="fin-sensitivity">
              <p className="text-[10px] uppercase tracking-[0.2em] ihg-micro mb-2" style={mono}>RevPAR sensitivity · occupancy × ADR</p>
              <div className="grid grid-cols-4 gap-1.5 text-center text-[10px] sm:text-[11px]" style={mono}>
                <span />
                {['$233 (₹22K)', '$254 (₹24K)', '$275 (₹26K)'].map((a) => (
                  <span key={a} className="ihg-micro py-1">{a}</span>
                ))}
                {[
                  { occ: '50%', vals: ['$116 (₹11K)', '$127 (₹12K)', '$138 (₹13K)'] },
                  { occ: '58%', vals: ['$135 (₹12.8K)', '$147 (₹13.9K)', '$160 (₹15.1K)'] },
                  { occ: '65%', vals: ['$151 (₹14.3K)', '$165 (₹15.6K)', '$179 (₹16.9K)'] },
                ].map((row) => (
                  <React.Fragment key={row.occ}>
                    <span className="ihg-micro py-1.5">{row.occ}</span>
                    {row.vals.map((v) => (
                      <span
                        key={v}
                        className={`py-1.5 rounded ${row.occ === '58%' && v === '$147 (₹13.9K)' ? 'bg-[#E6C76A]/15 text-[#E6C76A] border border-[#E6C76A]/40' : 'ihg-body bg-[#283348]/70'}`}
                      >
                        {v}
                      </span>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <p className="mt-4 text-[10px] ihg-micro tracking-wide" style={mono} data-testid="fin-disclaimer">
              Indicative figures for discussion only · FX: $1 = ₹94.5 · Full model available in the data room
            </p>

            <div className="mt-6 max-w-4xl grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="fin-glossary">
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-lg p-3.5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>RevPAR</p>
                <p className="text-[11px] text-white/90 mb-1" style={sans}>Revenue Per Available Room</p>
                <p className="text-[11px] ihg-body leading-snug" style={sans}>
                  ADR × Occupancy. The hotel industry's primary profitability metric — a single number that captures both pricing power and demand.
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-lg p-3.5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>ADR</p>
                <p className="text-[11px] text-white/90 mb-1" style={sans}>Average Daily Rate</p>
                <p className="text-[11px] ihg-body leading-snug" style={sans}>
                  The average price paid per occupied room per night. IHG distribution lifts ADR by filling previously empty mid-week nights at premium rates.
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-lg p-3.5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>IRR · MOIC</p>
                <p className="text-[11px] text-white/90 mb-1" style={sans}>Return & Money Multiple</p>
                <p className="text-[11px] ihg-body leading-snug" style={sans}>
                  IRR = annualised return %; MOIC = total multiple of invested capital returned at exit. 3.4× MOIC means every $1 invested returns $3.40 over the hold.
                </p>
              </div>
            </div>

            <div className="mt-5 max-w-4xl bg-[#1A2235] border border-[#E6C76A]/30 rounded-lg p-4" data-testid="fin-worked-example">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#E6C76A] mb-2" style={mono}>Worked example · a single 10-key house</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[12px] ihg-body" style={sans}>
                <div className="space-y-1">
                  <p className="text-white">Pre-acquisition (today)</p>
                  <p>ADR ₹14,500 × Occupancy 38% × 10 keys × 365 nights</p>
                  <p>= <span className="text-white">~₹20.1 L</span> annual room revenue · ₹4.4 L EBITDA @ 22% margin</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[#E6C76A]">Post · Year 3 (within IHG × GoHimalayan)</p>
                  <p>ADR ₹24,000 × Occupancy 58% × 10 keys × 365 nights</p>
                  <p>= <span className="text-white">~₹50.8 L</span> annual room revenue · ₹20.3 L EBITDA @ 40% margin</p>
                </div>
              </div>
              <p className="mt-3 pt-3 border-t border-[#3F4A66] text-[11px] ihg-body" style={sans}>
                <span className="text-[#E6C76A]">Net effect:</span> ~2.5× revenue growth, ~4.6× EBITDA growth per house.
                Replicated across 100 houses, this compounds into the fund&rsquo;s ~20% net IRR
                target over a 7–10 year hold.
              </p>
            </div>
          </div>
        </section>

        {/* 11 · THE ASK */}
        <section ref={setSlideRef(10)} id="slide-ask" className={slideClass} data-testid="slide-next-steps-container">
          <div className={innerClass}>
            <Eyebrow>11 · The Ask & Roadmap</Eyebrow>
            <SlideTitle>The First Flagships.<br />One Signature. A New Asset Class.</SlideTitle>
            <SlideSub>
              The immediate ask: a pilot cohort of ten intimate flagship houses —
              Bluehouse at the helm — brought under the IHG group as the first
              GoHimalayan Collection properties.
            </SlideSub>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 max-w-4xl" data-testid="ask-roadmap">
              {[
                { q: 'Phase I · Q4 2026', t: 'LOI, pilot & brand decision', d: '$3–5M first close · Bluehouse + 9 houses · IHG brand pathway chosen (Vignette · Indigo · dedicated sub-brand)' },
                { q: 'Phase II · Q3 2027', t: 'Scale across valleys', d: '$5–7M second close · 30 more houses · priced on Phase I trading data' },
                { q: 'Phase III · Q3 2028', t: 'Complete the Collective', d: '$7–9M final close · Collective 100 complete · exit horizon 2033–2035' },
              ].map((step) => (
                <div key={step.q} className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-5 hover:border-[#E6C76A]/50 transition-colors">
                  <p className="text-[#E6C76A] text-xs mb-2" style={mono}>{step.q}</p>
                  <p className="text-sm font-semibold text-white mb-1" style={sans}>{step.t}</p>
                  <p className="text-[11px] ihg-body leading-relaxed" style={sans}>{step.d}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-10">
              <a
                href="/pitch-ihg.pdf"
                download="GoHimalayan-IHG-Pitch.pdf"
                className="inline-flex items-center justify-center gap-2 bg-[#E6C76A] text-[#0B0F17] font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-[#E6C665] transition-colors"
                style={sans}
                data-testid="cta-download-pdf"
              >
                <Printer className="w-4 h-4" /> Download Deck (PDF)
              </a>
              <a
                href={WA_BRIEFING}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-[#E6C76A]/50 text-[#E6C76A] text-sm px-7 py-3.5 rounded-full hover:bg-[#E6C76A]/10 transition-colors"
                style={sans}
                data-testid="cta-schedule-briefing"
              >
                <MessageCircle className="w-4 h-4" /> Schedule Executive Briefing
              </a>
              <a
                href={WA_DATAROOM}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-[#3F4A66] ihg-body text-sm px-7 py-3.5 rounded-full hover:border-[#E6C76A]/40 hover:text-white transition-colors"
                style={sans}
                data-testid="cta-data-room"
              >
                <Target className="w-4 h-4" /> Access the Data Room
              </a>
            </div>
          </div>
        </section>

        {/* 12 · IHG LEADS */}
        <section ref={setSlideRef(11)} id="slide-ihg-leads" className={`${slideClass} ihg-grain`} data-testid="slide-ihg-leads-container">
          <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-[#E6C76A]/10 blur-[150px]" />
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#E6C76A]/8 blur-[140px]" />
          <div className={innerClass}>
            <Eyebrow>12 · The IHG Opportunity</Eyebrow>
            <SlideTitle>
              A Category <em className="italic text-[#E6C76A]">IHG</em> Can Lead —<br />
              Through GoHimalayan.
            </SlideTitle>
            <p className="text-sm md:text-base ihg-body leading-relaxed max-w-2xl" style={sans}>
              No global group owns the Himalaya yet. Marriott, Hyatt, and Accor have not
              entered intimate scale. IHG can plant the first flag — a dedicated Himalayan
              brand, 100 houses deep, with a travel portal pumping demand into it.
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-6 max-w-5xl" data-testid="ihg-leads-grid">
              <div className="bg-[#E6C76A]/8 border border-[#E6C76A]/40 rounded-xl p-4">
                <Crown className="w-4 h-4 text-[#E6C76A] mb-2" strokeWidth={1.5} />
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>First mover</p>
                <p className="text-xs sm:text-sm font-semibold text-white mb-0.5" style={sans}>Define the category</p>
                <p className="text-[11px] text-white/80 leading-snug" style={sans}>
                  100 IHG-flagged houses across the Indian Himalaya — the deepest intimate-scale footprint in experiential mountain luxury.
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <Home className="w-4 h-4 text-[#E6C76A] mb-2" strokeWidth={1.5} />
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>Zero capex</p>
                <p className="text-xs sm:text-sm font-semibold text-white mb-0.5" style={sans}>Balance-sheet light</p>
                <p className="text-[11px] ihg-body leading-snug" style={sans}>
                  Fund owns the equity and the capex. IHG earns distribution and loyalty economics without a dollar at risk.
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <Users className="w-4 h-4 text-[#E6C76A] mb-2" strokeWidth={1.5} />
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>Loyalty acquisition</p>
                <p className="text-xs sm:text-sm font-semibold text-white mb-0.5" style={sans}>A new audience</p>
                <p className="text-[11px] ihg-body leading-snug" style={sans}>
                  Indian HNI + global experiential traveller — a cohort IHG One under-indexes on. Delivered flagged, day one.
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <Leaf className="w-4 h-4 text-[#E6C76A] mb-2" strokeWidth={1.5} />
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>ESG at scale</p>
                <p className="text-xs sm:text-sm font-semibold text-white mb-0.5" style={sans}>A real impact story</p>
                <p className="text-[11px] ihg-body leading-snug" style={sans}>
                  Host-driven · solar-first · fragile-ecology stewardship. The 2027 ESG report writes itself.
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <Globe className="w-4 h-4 text-[#E6C76A] mb-2" strokeWidth={1.5} />
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>Export template</p>
                <p className="text-xs sm:text-sm font-semibold text-white mb-0.5" style={sans}>Replicable globally</p>
                <p className="text-[11px] ihg-body leading-snug" style={sans}>
                  Nepal · Bhutan · the Andes · the Alps. The GoHimalayan-within-IHG model is the first blueprint for institutionalising boutique heritage worldwide.
                </p>
              </div>
              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <Cpu className="w-4 h-4 text-[#E6C76A] mb-2" strokeWidth={1.5} />
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#E6C76A] mb-1" style={mono}>Tech moat</p>
                <p className="text-xs sm:text-sm font-semibold text-white mb-0.5" style={sans}>Owned middleware</p>
                <p className="text-[11px] ihg-body leading-snug" style={sans}>
                  GoHimalayan OS plugs legacy homes into Opera + CRS. Years of ground relationships — not easily replicated.
                </p>
              </div>
            </div>

            <div className="mt-5 max-w-4xl border-l-2 border-[#E6C76A]/60 pl-4" data-testid="ihg-leads-closing">
              <p className="text-sm md:text-base text-white leading-snug" style={pf}>
                Marriott owns the big city. Hyatt owns the resort.<br />
                The Himalaya is still open — <em className="italic text-[#E6C76A]">intimate scale is where the next decade is won.</em>
              </p>
            </div>

            <div className="flex items-center justify-between mt-5 pt-3 border-t border-[#3F4A66]/60">
              <div className="flex items-center gap-3">
                <GoHimalayanMark />
                <span className="text-[#263248]">×</span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/80" style={mono}>IHG Hotels & Resorts</span>
              </div>
              <Collective100Mark />
            </div>
            <p className="mt-4 text-[10px] uppercase tracking-[0.3em] ihg-micro" style={mono}>
              Strictly Confidential · Investor Relations & Strategic Brand Partnerships
            </p>
          </div>
        </section>

        {/* 13 · A MOVEMENT, NOT A DEAL — stakeholder flywheel */}
        <section ref={setSlideRef(12)} id="slide-movement" className={`${slideClass} ihg-grain`} data-testid="slide-movement-container">
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#E6C76A]/8 blur-[150px]" />
          <div className="absolute -bottom-40 -left-32 w-[480px] h-[480px] rounded-full bg-[#E6C76A]/6 blur-[140px]" />
          <div className={innerClass}>
            <Eyebrow>13 · A Movement, Not a Deal</Eyebrow>
            <SlideTitle>
              The First Hospitality Platform<br />
              Where <em className="italic text-[#E6C76A]">Everyone</em> Wins.
            </SlideTitle>
            <p className="text-sm md:text-base ihg-body leading-relaxed max-w-3xl" style={sans}>
              Imagine owners, hosts, and their teams choosing to be part of GoHimalayan —
              because it transforms their lives. Monetised assets, financially stable
              families, globally-trained teams, protected ecologies — a new category of
              hospitality the world has never seen.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-6 max-w-6xl" data-testid="movement-stakeholders">
              <div className="bg-[#283348]/90 border border-[#E6C76A]/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Home className="w-4 h-4 text-[#E6C76A]" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A]" style={mono}>Hosts & owners</p>
                </div>
                <p className="text-[13px] font-semibold text-white mb-1.5" style={sans}>
                  From custodians to shareholders — forever.
                </p>
                <ul className="space-y-1 text-[11.5px] ihg-body leading-relaxed" style={sans}>
                  <li>• <span className="text-white">Liquidity on day one</span> — monetise 40–50% of a life's work without selling it</li>
                  <li>• Retain the family seat and the host&rsquo;s voice at the table</li>
                  <li>• Pensionable asset — financially secure whether they upgrade capex or not</li>
                  <li>• Globally-branded house without the operational burden</li>
                </ul>
              </div>

              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-[#E6C76A]" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A]" style={mono}>Teams & villages</p>
                </div>
                <p className="text-[13px] font-semibold text-white mb-1.5" style={sans}>
                  Valley kitchens trained to global standards.
                </p>
                <ul className="space-y-1 text-[11.5px] ihg-body leading-relaxed" style={sans}>
                  <li>• IHG-grade hospitality training for every team member</li>
                  <li>• Career path from valley to the IHG global network</li>
                  <li>• Local supply chain priority — artisans, farms, drivers, guides</li>
                  <li>• Year-round employment stabilised by seasonal yield management</li>
                </ul>
              </div>

              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Leaf className="w-4 h-4 text-[#E6C76A]" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A]" style={mono}>Himalaya &amp; communities</p>
                </div>
                <p className="text-[13px] font-semibold text-white mb-1.5" style={sans}>
                  Stewardship built into the business model.
                </p>
                <ul className="space-y-1 text-[11.5px] ihg-body leading-relaxed" style={sans}>
                  <li>• Solar · water-harvesting · zero-plastic as acquisition pre-conditions</li>
                  <li>• Per-stay carbon ledger — institutional-grade ESG reporting</li>
                  <li>• Low-impact capex that preserves vernacular architecture</li>
                  <li>• A direct re-investment loop into trail, forest, and watershed care</li>
                </ul>
              </div>

              <div className="bg-[#283348]/90 border border-[#3F4A66] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Handshake className="w-4 h-4 text-[#E6C76A]" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E6C76A]" style={mono}>IHG · Investors · Guests</p>
                </div>
                <p className="text-[13px] font-semibold text-white mb-1.5" style={sans}>
                  Returns + reputation + the one-of-one guest journey.
                </p>
                <ul className="space-y-1 text-[11.5px] ihg-body leading-relaxed" style={sans}>
                  <li>• IHG — a new regional category to own, zero balance-sheet risk</li>
                  <li>• LPs — target ~20% net IRR with a genuine impact narrative</li>
                  <li>• Guests — intimate, host-driven luxury flagged by a global brand</li>
                  <li>• The press story writes itself: a first-of-its-kind partnership</li>
                </ul>
              </div>
            </div>

            <div className="mt-5 max-w-4xl border-l-2 border-[#E6C76A]/60 pl-4" data-testid="movement-closing">
              <p className="text-[15px] md:text-base text-white leading-snug" style={pf}>
                This is not a hotel deal. It is <em className="italic text-[#E6C76A]">a movement</em> —
                the first time a global group, a regional platform, and 100 host families
                have ever been aligned around the same roof.
              </p>
              <p className="mt-2 text-[12px] ihg-body" style={sans}>
                Every stakeholder is better off on day two than they were on day one.
                That is the only durable business model.
              </p>
            </div>

            <div className="flex items-center justify-between mt-5 pt-3 border-t border-[#3F4A66]">
              <div className="flex items-center gap-3">
                <GoHimalayanMark />
                <span className="text-[#3F4A66]">×</span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/80" style={mono}>IHG Hotels & Resorts</span>
              </div>
              <Collective100Mark />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
